import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Edit, Trash2 } from 'lucide-react';
import { API, useAuthHeader } from './hooks';
import Modal from './Modal';

const STATUS_OPTIONS = [
  { value: 'booked', label: 'Booked' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusBadge = (status) => {
  const styles = {
    booked: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
  };
  return styles[status] || 'bg-gray-100 text-gray-800';
};

const emptyForm = { date: '', time: '', client_name: '', client_email: '', status: 'booked', admin_notes: '' };

const AppointmentsManager = () => {
  const getAuthHeader = useAuthHeader();
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/appointments`, getAuthHeader());
      setItems(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openEdit = (item) => {
    setCurrent(item);
    setForm({
      date: item.date || '',
      time: item.time || '',
      client_name: item.client_name || '',
      client_email: item.client_email || '',
      status: item.status || 'booked',
      admin_notes: item.admin_notes || '',
    });
    setShowModal(true);
  };

  const handleQuickStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/appointments/${id}/status`, { status }, getAuthHeader());
      toast.success(`Marked as ${status}`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, getAuthHeader());
      toast.success('Appointment deleted');
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSubmit = async () => {
    try {
      await axios.patch(`${API}/appointments/${current.id}`, form, getAuthHeader());
      toast.success('Appointment updated');
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div data-testid="appointments-content">
      <h2 className="font-heading text-3xl mb-8 flex items-center gap-3">
        <Calendar size={28} /> Appointments
      </h2>

      {items.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 text-center text-gray-500">No appointments yet.</div>
      ) : (
        <div className="space-y-4">
          {items.map((appt) => (
            <div key={appt.id} className="bg-white border border-gray-200 p-6" data-testid={`appointment-item-${appt.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-4 mb-3">
                <div>
                  <h3 className="font-heading font-bold text-lg">{appt.client_name}</h3>
                  <p className="font-mono text-sm text-gray-500">{appt.client_email}</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-lg font-bold">{appt.date}</p>
                  <p className="font-mono text-sm">{appt.time}</p>
                </div>
                <span className={`font-mono text-xs uppercase px-3 py-1 rounded-full ${statusBadge(appt.status)}`}>
                  {appt.status}
                </span>
              </div>

              {appt.admin_notes && (
                <p className="font-body text-sm text-gray-600 italic mb-3">📝 {appt.admin_notes}</p>
              )}

              <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                <select
                  value={appt.status}
                  onChange={(e) => handleQuickStatus(appt.id, e.target.value)}
                  data-testid={`appointment-quick-status-${appt.id}`}
                  className="border border-gray-300 px-3 py-1 font-mono text-xs uppercase"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => openEdit(appt)}
                  data-testid={`appointment-edit-${appt.id}`}
                  className="flex items-center gap-1 bg-gray-100 px-3 py-1 font-mono text-xs uppercase hover:bg-gray-200"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => handleDelete(appt.id)}
                  data-testid={`appointment-delete-${appt.id}`}
                  className="flex items-center gap-1 bg-red-100 text-red-700 px-3 py-1 font-mono text-xs uppercase hover:bg-red-200"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Edit Appointment"
        footer={
          <button
            onClick={handleSubmit}
            disabled={!form.client_name || !form.date || !form.time}
            data-testid="appointment-form-submit"
            className="w-full bg-black text-white py-4 font-bold text-sm uppercase tracking-wider hover:bg-gray-800 disabled:opacity-50"
          >
            Save Changes
          </button>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Date *</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="appointment-form-date"
                className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Time *</label>
              <input
                type="text"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                placeholder="e.g. 2:00 PM"
                data-testid="appointment-form-time"
                className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              />
            </div>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Client Name *</label>
            <input
              type="text"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              data-testid="appointment-form-name"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Client Email</label>
            <input
              type="email"
              value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })}
              data-testid="appointment-form-email"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="appointment-form-status"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider mb-1 block">Internal Notes</label>
            <textarea
              value={form.admin_notes}
              onChange={(e) => setForm({ ...form, admin_notes: e.target.value })}
              rows={3}
              data-testid="appointment-form-notes"
              className="w-full border-2 border-gray-300 px-4 py-3 focus:border-black focus:outline-none resize-none"
              placeholder="Private notes for studio team only"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AppointmentsManager;
